import { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Card, Message, Spinner } from '@iwoca/orion';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import clsx from 'clsx';
import {
  FileUpload,
  TFileUploadValue,
} from 'components/Registration/FileUpload/FileUpload';
import { wrapFileInArray } from 'components/Registration/FileUpload/FileUpload.helpers';
import {
  handleSaveDocuments,
  handlePatchOnboardingFilesUploaded,
} from 'components/Registration/IntroducerOnboarding/RegistrationForm/registrationForm.helpers';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { PATH_NAMES } from 'utils/routes/pathNames';
import sharedStyles from '../Registration/IntroducerOnboarding/RegistrationForm/RegistrationForm.module.css';
import { useRegistrationDocuments } from './RegistrationDocuments.helpers';
import styles from './RegistrationDocuments.module.css';
import { TRegistrationDocumentsFormValues } from './RegistrationDocuments.types';

export const RegistrationDocuments = () => {
  const navigate = useNavigate();
  const {
    missingDocuments,
    isLoading,
    introducerId,
    introducerAccountId,
    schema,
  } = useRegistrationDocuments();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasUploadError, setHasUploadError] = useState(false);
  const [hasUploadedDocs, setHasUploadedDocs] = useState(false);

  // Handling redirection in case someone accesses the page directly by typing in the URL
  useEffect(() => {
    if (!isLoading && missingDocuments.length === 0) {
      navigate(PATH_NAMES.home());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, missingDocuments]);

  const methods = useForm<TRegistrationDocumentsFormValues>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (values: TRegistrationDocumentsFormValues) => {
    const selectedFiles = missingDocuments
      .map((doc) => values[doc.field])
      .filter((file): file is TFileUploadValue => Boolean(file));

    if (introducerId) {
      setHasUploadError(false);
      setIsSubmitting(true);
      try {
        // when re-submitting form, if docs have already been uploaded successfully, don't try again - skip to retry the patch
        if (!hasUploadedDocs) {
          await handleSaveDocuments({
            selectedFiles,
            introducerId,
          });
        }
        // await success of the handleSaveDocuments and mark that the documents have been uploaded
        setHasUploadedDocs(true);

        if (introducerAccountId) {
          await handlePatchOnboardingFilesUploaded({ introducerAccountId });
        }
        navigate(PATH_NAMES.home());
      } catch {
        setHasUploadError(true);
      }
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <Spinner className={styles.loader} />;
  }

  return (
    <div className={styles.pageContent}>
      <h2 className={styles.heading}>
        <Trans>Your account's ready - just one more step</Trans>
      </h2>
      <p className={styles.subHeading}>
        <Trans>
          Your account has been created, but there was an issue uploading your
          documents. Please re-upload them below to continue.
        </Trans>
      </p>

      <FormProvider {...methods}>
        <form className={styles.form} onSubmit={methods.handleSubmit(onSubmit)}>
          <Card className={styles.card}>
            {missingDocuments.map((doc) => (
              <FileUpload
                fileDropClassName={styles.fileDrop}
                cardClassName={styles.fileDropCard}
                key={doc.docType}
                docType={doc.docType}
                label={doc.label}
                instruction={doc.instruction}
                uploadedLabel={t`Document uploaded`}
                selectedFiles={wrapFileInArray(methods.watch(doc.field))}
                errorMessage={methods.formState.errors?.[doc.field]?.message}
                onChange={(files) => methods.setValue(doc.field, files[0])}
              />
            ))}
          </Card>
          <Button
            variant="primary"
            size="small"
            type="submit"
            loading={isSubmitting}
            className={clsx(sharedStyles.submitButton, styles.submit)}
          >
            <Trans>Submit documents</Trans>
          </Button>
          {hasUploadError && (
            <Message
              className={styles.error}
              type="error"
              message={t`Something went wrong, please refresh the page and try again.`}
            />
          )}
        </form>
      </FormProvider>
    </div>
  );
};
